/**
 * Hostile behaviour (IMPLEMENTATION_PLAN.md E2).
 *
 * Every type is a small seeded state machine that writes a goal point and a speed; the
 * shared kinematics in ships.ts do the driving. Keeping "what do I want" separate from
 * "how do I get there" is what stops each new enemy type from re-implementing steering.
 *
 * The design intent behind the weave phases: an enemy that drives straight at its victim
 * is a trivial target, and a trivially-hit target makes a good gun feel like a bad one.
 * Lateral motion at range is what turns the autocannon's travel time into a skill.
 */

import type { Vessel, EntityId } from './types';
import type { Rng } from './rng';
import { vesselSpec, SUBMERGED_DEPTHS } from '../content/vessels';
import { KRAKEN } from '../content/boss';
import { findVessel, rangeToRig, mustKeepClearOfRig } from './ships';
import { RIG_KEEP_OUT_METRES } from './constants';

export interface AiContext {
  vessels: readonly Vessel[];
  time: number;
  dt: number;
  rng: Rng;
  /** Called when the vessel wants to shoot at a point. */
  fire: (shooter: Vessel, targetId: EntityId | null, aimX: number, aimY: number, aimZ: number) => void;
}

/**
 * Target doctrine, shared by every hostile: go for the nearest live inbound friendly;
 * fall back to the rig for types that are allowed to attack it. Called whenever a vessel
 * has no target or its target stopped being valid.
 */
export function selectTarget(self: Vessel, vessels: readonly Vessel[]): EntityId | null {
  let best: Vessel | null = null;
  let bestDist = Infinity;
  for (const v of vessels) {
    if (v.faction !== 'friendly') continue;
    // A ship that has reached safety is out of the fight, by design.
    if (v.state === 'dead' || v.state === 'sinking' || v.state === 'departing') continue;
    /*
     * Past the Safe Line she is untouchable, so hunting her is wasted effort — and, worse,
     * it looks broken: a pack pouring fire into a hull the HUD has just called SAFE, for
     * no damage. Dropping her here makes the break-off free, and it reads as the raiders
     * refusing to follow her in under the platform's guns.
     */
    if (v.safe) continue;
    const d = Math.hypot(v.pos.x - self.pos.x, v.pos.z - self.pos.z);
    if (d < bestDist) { bestDist = d; best = v; }
  }
  return best ? best.id : null;
}

/** Types willing to shoot at the rig when no friendly is available. */
function attacksRig(v: Vessel): boolean {
  return v.kind === 'gunboat' || v.kind === 'fac' || v.kind === 'corvette';
}

/**
 * Where rig-directed fire is aimed: the waterline at the platform's legs.
 *
 * It used to be the topsides (y ≈ 90), and that made every rig attack in the game
 * cosmetic: a round aimed 90 m up passes over the deck still climbing relative to the
 * sea and splashes hundreds of metres beyond the platform — outside the 55 m ring that
 * `stepProjectilesAndDamage` credits against the rig. Measured before this fix: a
 * gunboat on the rig, four minutes, forty-three rounds, zero integrity lost. The water
 * at the base is the only thing the damage model can actually see.
 */
const RIG_AIM = { x: 0, y: 0, z: 0 } as const;

export function stepHostileAi(v: Vessel, ctx: AiContext): void {
  if (v.state === 'sinking' || v.state === 'dead') return;

  v.weaponCooldown = Math.max(0, v.weaponCooldown - ctx.dt);
  v.stateTimer += ctx.dt;

  // Re-target when the current one is gone, safe or dead. A rig-mission vessel never
  // picks a ship at all: its business is the platform, whatever else is afloat.
  if (v.mission !== 'rig') {
    const target = findVessel(ctx.vessels, v.targetId);
    const targetValid = target && !target.safe
      && target.state !== 'dead' && target.state !== 'sinking' && target.state !== 'departing';
    if (!targetValid) {
      v.targetId = selectTarget(v, ctx.vessels);
    }
  }

  switch (v.kind) {
    case 'skiff': stepSkiff(v, ctx); break;
    case 'gunboat': stepGunboat(v, ctx); break;
    case 'fac': stepFac(v, ctx); break;
    case 'corvette': stepCorvette(v, ctx); break;
    case 'minisub': stepMinisub(v, ctx); break;
    case 'tentacle': stepTentacle(v, ctx); break;
    // The Kraken steers from its own phase machine (sim/kraken.ts), not from here.
    case 'kraken': break;
    default: break;
  }

  keepGoalOutsideRig(v);
}

/**
 * Never *aim* inside the platform's keep-out ring.
 *
 * `enforceRigKeepOut` in ships.ts is the guarantee; this is what stops a hull grinding
 * against it. Most doctrines already stand well off — the skiff orbits at 260 m, the
 * gunboat at 600, the corvette at 2600 — but the FAC's attack run sets its goal *through*
 * the target, so a rig-directed pass aims at the origin itself and would drive straight
 * under the deck. Applied centrally, after the behaviour has spoken, so every future
 * doctrine inherits it rather than having to remember.
 *
 * The bearing of the goal is preserved: the vessel still commits to the same side of the
 * rig, it simply stops short of the structure.
 */
function keepGoalOutsideRig(v: Vessel): void {
  if (!mustKeepClearOfRig(v)) return;
  const d = Math.hypot(v.goalX, v.goalZ);
  if (d >= RIG_KEEP_OUT_METRES) return;
  if (d < 1e-3) {
    // A goal exactly on the platform has no bearing to keep; hold station where it is.
    v.goalX = v.pos.x;
    v.goalZ = v.pos.z;
    return;
  }
  const scale = RIG_KEEP_OUT_METRES / d;
  v.goalX *= scale;
  v.goalZ *= scale;
}

/**
 * Skiff: converge on the assigned friendly, then orbit-weave at 200–400 m firing RPGs.
 * The weave is a sine on the approach bearing, so its lateral speed peaks at the crossing
 * points — the moments a good gunner waits for.
 */
function stepSkiff(v: Vessel, ctx: AiContext): void {
  const spec = vesselSpec(v.kind);
  const target = findVessel(ctx.vessels, v.targetId);

  // A rig-mission skiff is here for the platform itself; the convoy is not its problem.
  const aim = v.mission === 'rig'
    ? { ...RIG_AIM, id: null }
    : target
      ? { x: target.pos.x, y: target.pos.y + 6, z: target.pos.z, id: target.id }
      : null;

  if (!aim) {
    // Nothing to attack: mill about outside the rig rather than beaching on it.
    const bearing = Math.atan2(v.pos.x, v.pos.z) + 0.35 * ctx.dt;
    v.goalX = Math.sin(bearing) * 1400;
    v.goalZ = Math.cos(bearing) * 1400;
    v.speed = spec.speed * 0.5;
    return;
  }

  const dx = aim.x - v.pos.x;
  const dz = aim.z - v.pos.z;
  const dist = Math.hypot(dx, dz);
  const toTarget = Math.atan2(dx, dz);

  if (dist > 420) {
    v.state = 'inbound';
    // Weave on the run-in: a small oscillation perpendicular to the approach.
    const weave = Math.sin(ctx.time * 0.9 + v.id * 2.1) * 0.32;
    const heading = toTarget + weave;
    v.goalX = v.pos.x + Math.sin(heading) * 600;
    v.goalZ = v.pos.z + Math.cos(heading) * 600;
    v.speed = spec.speed;
  } else {
    v.state = 'attacking';
    // Orbit: aim at a point on a circle around the victim, advancing the phase each tick.
    const orbitRadius = 260;
    const phase = ctx.time * 0.42 + v.id * 1.3;
    v.goalX = aim.x + Math.sin(phase) * orbitRadius;
    v.goalZ = aim.z + Math.cos(phase) * orbitRadius;
    v.speed = spec.speed * 0.82;

    if (v.weaponCooldown <= 0 && dist <= spec.weaponRange) {
      // Aimed with error — a skiff crew in a seaway is not a marksman.
      const err = 0.05;
      const aimX = aim.x + (ctx.rng.next() - 0.5) * dist * err;
      const aimZ = aim.z + (ctx.rng.next() - 0.5) * dist * err;
      ctx.fire(v, aim.id, aimX, aim.y, aimZ);
      v.weaponCooldown = spec.weaponInterval * (0.8 + ctx.rng.next() * 0.4);
    }
  }
}

/** Gunboat: stand-off figure-eight strafing; turns on the rig if no friendly is near. */
function stepGunboat(v: Vessel, ctx: AiContext): void {
  const spec = vesselSpec(v.kind);
  const target = findVessel(ctx.vessels, v.targetId);
  const targetInBubble = target
    && Math.hypot(target.pos.x - v.pos.x, target.pos.z - v.pos.z) < 2000;

  const aim = targetInBubble && target
    ? { x: target.pos.x, y: target.pos.y + 6, z: target.pos.z, id: target.id }
    : attacksRig(v) || v.mission === 'rig'
      ? { ...RIG_AIM, id: null }
      : null;

  if (!aim) {
    v.speed = spec.speed * 0.4;
    return;
  }

  const dist = Math.hypot(aim.x - v.pos.x, aim.z - v.pos.z);
  const standOffRange = 600;
  // Figure-eight: a lissajous around the stand-off circle.
  const phase = ctx.time * 0.3 + v.id;
  const bearing = Math.atan2(v.pos.x - aim.x, v.pos.z - aim.z);
  v.goalX = aim.x + Math.sin(bearing + Math.sin(phase) * 0.9) * standOffRange;
  v.goalZ = aim.z + Math.cos(bearing + Math.sin(phase) * 0.9) * standOffRange;
  v.speed = spec.speed * (dist > standOffRange * 1.4 ? 1 : 0.7);
  v.state = dist < spec.weaponRange ? 'attacking' : 'inbound';

  if (v.weaponCooldown <= 0 && dist <= spec.weaponRange) {
    const err = 0.03;
    ctx.fire(
      v, aim.id,
      aim.x + (ctx.rng.next() - 0.5) * dist * err,
      aim.y,
      aim.z + (ctx.rng.next() - 0.5) * dist * err,
    );
    v.weaponCooldown = spec.weaponInterval;
  }
}

/** FAC: serpentine to 1.2 km, rocket volley, jink away, repeat. */
function stepFac(v: Vessel, ctx: AiContext): void {
  const spec = vesselSpec(v.kind);
  const target = findVessel(ctx.vessels, v.targetId);
  const aim = target
    ? { x: target.pos.x, y: target.pos.y + 6, z: target.pos.z, id: target.id }
    : { ...RIG_AIM, id: null };

  const dist = Math.hypot(aim.x - v.pos.x, aim.z - v.pos.z);

  if (v.state === 'evading') {
    if (v.stateTimer > 8) { v.state = 'inbound'; v.stateTimer = 0; }
    const away = Math.atan2(v.pos.x - aim.x, v.pos.z - aim.z);
    v.goalX = v.pos.x + Math.sin(away) * 900;
    v.goalZ = v.pos.z + Math.cos(away) * 900;
    v.speed = spec.speed;
    return;
  }

  const serpentine = Math.sin(ctx.time * 0.55 + v.id * 1.9) * 0.45;
  const toTarget = Math.atan2(aim.x - v.pos.x, aim.z - v.pos.z);
  const heading = toTarget + serpentine;
  v.goalX = v.pos.x + Math.sin(heading) * 800;
  v.goalZ = v.pos.z + Math.cos(heading) * 800;
  v.speed = spec.speed;
  v.state = dist <= spec.weaponRange ? 'attacking' : 'inbound';

  if (v.weaponCooldown <= 0 && dist <= spec.weaponRange) {
    // A volley of four arcing rockets, fanned.
    for (let i = 0; i < 4; i++) {
      const spread = (i - 1.5) * 0.012;
      ctx.fire(
        v, aim.id,
        aim.x + Math.sin(toTarget + spread) * 20,
        aim.y,
        aim.z + Math.cos(toTarget + spread) * 20,
      );
    }
    v.weaponCooldown = spec.weaponInterval;
    v.state = 'evading';
    v.stateTimer = 0;
  }
}

/** Corvette: holds 3 km, turns broadside, alternates volleys between rig and friendlies. */
function stepCorvette(v: Vessel, ctx: AiContext): void {
  const spec = vesselSpec(v.kind);
  const range = rangeToRig(v);
  /*
   * Comfortably inside her 3000 m weapon range, and it has to be. At 3000 the tangent
   * track settled at ~3255 m — just outside her own gun — and the `range <= weaponRange`
   * gate below never opened: the corvette shipped in two shifts having never fired a
   * volley at anything. Measured, not supposed.
   */
  const hold = 2600;

  // Come to the holding range, then present a broadside by steering along the tangent.
  const bearing = Math.atan2(v.pos.x, v.pos.z);
  if (range > hold * 1.1) {
    v.goalX = Math.sin(bearing) * hold;
    v.goalZ = Math.cos(bearing) * hold;
    v.speed = spec.speed;
    v.state = 'inbound';
  } else {
    const tangent = bearing + Math.PI * 0.5;
    v.goalX = v.pos.x + Math.sin(tangent) * 1200;
    v.goalZ = v.pos.z + Math.cos(tangent) * 1200;
    v.speed = spec.speed * 0.55;
    v.state = 'attacking';
  }

  if (v.weaponCooldown <= 0 && range <= spec.weaponRange) {
    const target = findVessel(ctx.vessels, v.targetId);
    // Alternate: even volleys at the rig, odd at whatever friendly is inbound.
    const atRig = !target || Math.floor(ctx.time / spec.weaponInterval) % 2 === 0;
    const aim = atRig
      ? { ...RIG_AIM, id: null }
      : { x: target.pos.x, y: target.pos.y + 8, z: target.pos.z, id: target.id };
    for (let i = 0; i < 3; i++) {
      const jitter = (ctx.rng.next() - 0.5) * 40;
      ctx.fire(v, aim.id, aim.x + jitter, aim.y, aim.z + jitter);
    }
    v.weaponCooldown = spec.weaponInterval;
  }
}

/**
 * Mini-sub: submerged ingress, a periscope pause that is the player's counterplay window,
 * a torpedo, then reposition on a track that stays inside the depth-charge envelope. The
 * encounter is only fair because that last constraint holds.
 */
function stepMinisub(v: Vessel, ctx: AiContext): void {
  const spec = vesselSpec(v.kind);
  const range = rangeToRig(v);
  const target = findVessel(ctx.vessels, v.targetId);

  if (v.state === 'inbound') {
    // Running deep: nothing on radar, nothing to shoot at, nothing to see — and too deep
    // for the shallow fuse to reach, which is what the deep setting is for.
    v.submerged = true;
    v.submergedDepth = SUBMERGED_DEPTHS.minisubDeep;
    const want = 950;
    const bearing = Math.atan2(v.pos.x, v.pos.z);
    v.goalX = Math.sin(bearing) * want;
    v.goalZ = Math.cos(bearing) * want;
    v.speed = spec.speed;
    if (Math.abs(range - want) < 120) { v.state = 'attacking'; v.stateTimer = 0; }
    return;
  }

  if (v.state === 'attacking') {
    /*
     * Periscope up. This is the counterplay window and the only reason the encounter is
     * fair: for thirty seconds the boat is on radar, visible, and killable by gunfire.
     * Miss it and she is back under, where only the projector can reach her.
     */
    v.submerged = false;
    v.submergedDepth = 0;
    v.speed = spec.speed * 0.25;
    if (v.stateTimer > 30 && v.weaponCooldown <= 0) {
      const aim = target
        ? { x: target.pos.x, y: 0, z: target.pos.z, id: target.id }
        : { x: 0, y: 0, z: 0, id: null };
      ctx.fire(v, aim.id, aim.x, aim.y, aim.z);
      v.weaponCooldown = spec.weaponInterval;
      v.state = 'evading';
      v.stateTimer = 0;
    }
    return;
  }

  // Evading: back under, circling inside the base projector envelope (0.6–1.1 km) — and
  // shallower than she came in, which is what makes the counterattack answerable.
  v.submerged = true;
  v.submergedDepth = SUBMERGED_DEPTHS.minisubShallow;
  const phase = ctx.time * 0.06 + v.id;
  const orbit = 850;
  v.goalX = Math.sin(phase) * orbit;
  v.goalZ = Math.cos(phase) * orbit;
  v.speed = spec.speed * 0.8;
  if (v.stateTimer > 45) { v.state = 'attacking'; v.stateTimer = 0; }
}


/**
 * A tentacle: reach the hull it surfaced beside, then crush it.
 *
 * It barely manoeuvres — it does not need to, because it comes up already alongside. The
 * behaviour that matters is that it does not stop: once it has hold, damage is continuous
 * until the limb is severed, so the player is on a clock rather than in a firefight.
 * With no ship to take it turns on the rig legs instead, which is what makes ignoring
 * them expensive.
 */
function stepTentacle(v: Vessel, ctx: AiContext): void {
  const spec = vesselSpec(v.kind);
  const target = findVessel(ctx.vessels, v.targetId);

  const aim = target
    ? { x: target.pos.x, y: target.pos.y + 4, z: target.pos.z, id: target.id }
    : { x: 0, y: 12, z: 0, id: null };

  /*
   * A limb reaching for the rig has a longer arm than one grappling a hull: it hooks
   * the deck edge from where it stands rather than pulling alongside, and the reach is
   * the wrap's own trigger range so the grip the AI credits is the grip the mesh and
   * the hit volume show. Ship grapples keep the short reach — those limbs are dragging
   * a hull down at arm's length.
   */
  const reach = target ? spec.weaponRange : KRAKEN.assaultRangeFar + 12;
  const dist = Math.hypot(aim.x - v.pos.x, aim.z - v.pos.z);
  v.goalX = aim.x;
  v.goalZ = aim.z;
  // Slow while chasing a hull, slower still with hold of one — and PLANTED against the
  // rig: a limb that crept while hooked dragged its own hit volume out from under the
  // player's aim, and by the end of the assault stood fat and shapeless at the jacket.
  v.speed = dist > reach ? spec.speed : target ? spec.speed * 0.15 : 0;
  v.state = dist <= reach ? 'attacking' : 'inbound';

  if (v.weaponCooldown <= 0 && dist <= reach) {
    ctx.fire(v, aim.id, aim.x, aim.y, aim.z);
    v.weaponCooldown = spec.weaponInterval;
  }
}
